import { type DecisionSheetView, type ReorderDecisionQuestionsInput } from '@apm/contracts';
import { decisionSections } from './model';

/**
 * Thứ tự trong trình soạn — APM-DSHEET-d. Một danh sách **phẳng** gồm tiêu đề
 * nhóm và câu, để kéo một câu sang nhóm khác chỉ là kéo nó xuống dưới tiêu đề
 * nhóm ấy (một `SortableContext`, không phải nhiều ngăn chứa lồng nhau).
 *
 * Tiêu đề nhóm không kéo được, chỉ là mốc: câu thuộc nhóm của tiêu đề gần
 * nhất phía trên nó.
 */

export const UNGROUPED_ROW = 'group:__none__';

export function groupRowId(groupId: string | null): string {
  return groupId === null ? UNGROUPED_ROW : `group:${groupId}`;
}

export function isGroupRow(rowId: string): boolean {
  return rowId.startsWith('group:');
}

/**
 * Câu mới chưa lưu (APM-DSX-e) — một hàng **không kéo được** ở cuối nhóm của
 * nó, cho tới khi server có câu ấy. Không bao giờ đi vào lệnh sắp lại.
 */
const DRAFT_PREFIX = 'draft:';

export function draftRowId(tempId: string): string {
  return `${DRAFT_PREFIX}${tempId}`;
}

export function isDraftRow(rowId: string): boolean {
  return rowId.startsWith(DRAFT_PREFIX);
}

export function draftTempId(rowId: string): string {
  return rowId.slice(DRAFT_PREFIX.length);
}

/** Chèn hàng câu nháp vào cuối nhóm của nó (trước tiêu đề nhóm kế tiếp). */
export function withDraftRows(
  rows: readonly string[],
  drafts: readonly { readonly tempId: string; readonly groupId: string | null }[],
): string[] {
  const out = [...rows];
  for (const draft of drafts) {
    const header = out.indexOf(groupRowId(draft.groupId));
    if (header < 0) continue;
    let at = header + 1;
    while (at < out.length && !isGroupRow(out[at]!)) at += 1;
    out.splice(at, 0, draftRowId(draft.tempId));
  }
  return out;
}

/** Hàng cuối của một nhóm (câu cuối, hoặc chính tiêu đề nếu nhóm rỗng) — chỗ đặt "+ Câu hỏi". */
export function isGroupTail(rows: readonly string[], index: number): boolean {
  const next = rows[index + 1];
  return next === undefined || isGroupRow(next);
}

/** Hàng hiện tại: mỗi nhóm (kể cả rỗng) rồi câu của nó; "Chưa xếp nhóm" luôn ở cuối. */
export function editorRows(view: Pick<DecisionSheetView, 'groups' | 'questions'>): string[] {
  const sections = decisionSections(view, 'all', true);
  const rows: string[] = [];
  for (const section of sections) {
    rows.push(groupRowId(section.group?.id ?? null));
    for (const question of section.questions) rows.push(question.id);
  }
  if (!rows.includes(UNGROUPED_ROW)) rows.push(UNGROUPED_ROW);
  return rows;
}

/**
 * Hàng sau khi kéo ⇒ input của `ReorderDecisionQuestions`: mỗi câu mang nhóm
 * của tiêu đề gần nhất phía trên và vị trí trong nhóm ấy. Câu bị thả lên trên
 * mọi tiêu đề thì vào nhóm đầu tiên — không có "trước nhóm I".
 */
export function reorderInput(rows: readonly string[]): ReorderDecisionQuestionsInput {
  const firstHeader = rows.find(isGroupRow);
  const groupOf = (header: string | undefined): string | null =>
    header === undefined || header === UNGROUPED_ROW ? null : header.slice('group:'.length);

  let current = groupOf(firstHeader);
  const counters = new Map<string | null, number>();
  const items: ReorderDecisionQuestionsInput['items'] = [];
  for (const row of rows) {
    if (isDraftRow(row)) continue;
    if (isGroupRow(row)) {
      current = groupOf(row);
      continue;
    }
    const position = counters.get(current) ?? 0;
    counters.set(current, position + 1);
    items.push({ questionId: row, groupId: current, position });
  }
  return { items };
}

const ROMAN: readonly [number, string][] = [
  [10, 'X'],
  [9, 'IX'],
  [5, 'V'],
  [4, 'IV'],
  [1, 'I'],
];

function toRoman(value: number): string {
  let rest = value;
  let out = '';
  for (const [size, glyph] of ROMAN) {
    while (rest >= size) {
      out += glyph;
      rest -= size;
    }
  }
  return out;
}

function fromRoman(code: string): number | null {
  if (!/^[IVX]+$/.test(code)) return null;
  for (let value = 1; value <= 39; value += 1) if (toRoman(value) === code) return value;
  return null;
}

/** Mã nhóm kế tiếp theo mẫu nhóm cuối: `III` ⇒ `IV`, `N-2` ⇒ `N-3`; chưa có ⇒ `I`. */
export function nextGroupCode(codes: readonly string[]): string {
  const last = codes.at(-1);
  if (last === undefined) return 'I';
  const roman = fromRoman(last);
  if (roman !== null) return toRoman(roman + 1);
  const match = last.match(/^(.*?)(\d+)$/);
  if (match) {
    const [, prefix, digits] = match;
    return `${prefix}${String(Number(digits) + 1).padStart(digits!.length, '0')}`;
  }
  return `${last}-2`;
}
